import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Container,
  Paper,
  Avatar,
  Typography,
  IconButton,
  TextField,
  CircularProgress,
  Alert,
  Badge,
  Menu,
  MenuItem,
  Divider,
  Chip,
  Button
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Send as SendIcon,
  MoreVert as MoreVertIcon,
  VolumeOff as MuteIcon,
  Block as BlockIcon,
  Flag as ReportIcon,
  FiberManualRecord as OnlineDotIcon,
  CheckCircle as VerifiedIcon,
  Check as CheckIcon,
  DoneAll as DoneAllIcon,
  Warning as WarningIcon,
  ChatBubbleOutline as ChatBubbleIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { format } from 'date-fns';
import apiService from '../../services/apiService';
import { useSocket } from '../../contexts/SocketContext';
import { useAuth } from '../../contexts/AuthContext';
import { useUserProfile } from '../../contexts/UserProfileContext';
import LegalGateModal from '../../components/Chat/LegalGateModal';
import TypingIndicator from '../../components/Chat/TypingIndicator';
import ReportMessageModal from '../../components/Chat/ReportMessageModal';
import TranslatingText from '../../components/Common/TranslatingText';
import { formatDate, formatRelativeDate, getFullImageUrl, getInitials } from '../../utils/helpers';

// Message type from API (with string dates)
interface ApiMessage {
  id: string;
  senderId: string;
  receiverId: string;
  content: string;
  status: 'SENT' | 'DELIVERED' | 'READ';
  createdAt: string;
  deliveredAt?: string;
  readAt?: string;
  contextType?: string;
  contextId?: string;
  sender?: {
    id: string;
    firstName: string;
    lastName: string;
    preferredName?: string;
    profilePicture?: string;
  };
}

// Message type for Socket (with Date objects)
interface SocketMessage {
  id: string;
  senderId: string;
  receiverId: string;
  content: string;
  status: 'SENT' | 'DELIVERED' | 'READ';
  createdAt: Date | string;
  deliveredAt?: Date | string;
  readAt?: Date | string;
  contextType?: string;
  contextId?: string;
  sender?: {
    id: string;
    firstName: string;
    lastName: string;
    preferredName?: string;
    profilePicture?: string;
  };
}

interface PartnerInfo {
  id: string;
  firstName: string;
  lastName: string;
  preferredName?: string;
  profilePicture?: string;
  isDriver: boolean;
  driverRating?: number;
  passengerRating?: number;
  lastSeen?: string;
}

const ChatPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { partnerId } = useParams<{ partnerId: string }>();
  const { user } = useAuth();
  const { openProfile } = useUserProfile();
  const {
    isConnected,
    isOnline,
    sendMessage,
    markAsDelivered,
    markAsRead,
    startTyping,
    stopTyping,
    onMessageReceive,
    onMessageStatusUpdate,
    onTypingIndicator
  } = useSocket();

  // State from navigation
  const contextType = location.state?.contextType;
  const contextId = location.state?.contextId;
  const partnerName = location.state?.partnerName;

  // State
  const [messages, setMessages] = useState<ApiMessage[]>([]);
  const [partner, setPartner] = useState<PartnerInfo | null>(null);
  const [contextDetails, setContextDetails] = useState<any>(null);
  const [messageInput, setMessageInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const [oldestTimestamp, setOldestTimestamp] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [typingUsers, setTypingUsers] = useState<Set<string>>(new Set());
  const [showLegalGate, setShowLegalGate] = useState(false);
  const [hasConsent, setHasConsent] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [selectedMessageForReport, setSelectedMessageForReport] = useState<string | null>(null);
  const [isBlocked, setIsBlocked] = useState(false);
  const [supportTicketStatus, setSupportTicketStatus] = useState<string | null>(null);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const messageInputRef = useRef<HTMLInputElement>(null);
  const [isUserAtBottom, setIsUserAtBottom] = useState(true);
  const shouldAutoScrollRef = useRef(true);

  // Check if user is near bottom of scroll container
  const checkIfAtBottom = useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container) return false;

    const threshold = 100; // pixels from bottom
    const isAtBottom =
      container.scrollHeight - container.scrollTop - container.clientHeight < threshold;

    return isAtBottom;
  }, []);

  // Scroll to bottom smoothly
  const scrollToBottom = useCallback((force = false) => {
    if (force || shouldAutoScrollRef.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      setIsUserAtBottom(true);
      shouldAutoScrollRef.current = true;
    }
  }, []);

  // Scroll to bottom instantly (for initial load)
  const scrollToBottomInstant = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
    setIsUserAtBottom(true);
    shouldAutoScrollRef.current = true;
  }, []);

  // Handle scroll events
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const atBottom = checkIfAtBottom();
      setIsUserAtBottom(atBottom);
      shouldAutoScrollRef.current = atBottom;
    };

    container.addEventListener('scroll', handleScroll);
    // Also check on resize
    window.addEventListener('resize', handleScroll);

    return () => {
      container.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, [checkIfAtBottom]);

  // Check consent status
  const checkConsent = async () => {
    if (!contextType || !contextId) {
      setHasConsent(true); // No context = general chat, no consent needed
      return;
    }

    try {
      const response: any = await apiService.get(`/chat-consent/check/${contextType}/${contextId}`);
      if (response.success && response.hasConsent) {
        setHasConsent(true);
      } else {
        setShowLegalGate(true);
      }
    } catch (err) {
      console.error('Error checking consent:', err);
      setShowLegalGate(true); // Show gate on error to be safe
    }
  };

  // Check if blocked
  const checkBlockStatus = async () => {
    if (!partnerId) return;

    try {
      const response: any = await apiService.get(`/message-actions/is-blocked/${partnerId}`);
      if (response.success && response.isBlocked) {
        setIsBlocked(true);
      }
    } catch (err) {
      console.error('Error checking block status:', err);
    }
  };

  // Fetch partner info
  const fetchPartner = async () => {
    if (!partnerId) return;

    // Set initial info if we have partnerName from route params
    if (partnerName) {
      const [firstName, ...lastNameParts] = partnerName.split(' ');
      setPartner(prev => ({
        ...prev,
        id: partnerId,
        firstName,
        lastName: lastNameParts.join(' '),
        isDriver: prev?.isDriver || false
      }));
    }

    try {
      // Try to fetch user details from auth endpoint
      const response: any = await apiService.get(`/auth/users/${partnerId}`);
      if (response.success && response.data) {
        setPartner({
          id: partnerId,
          firstName: response.data.firstName || 'User',
          lastName: response.data.lastName || '',
          profilePicture: response.data.profilePicture,
          isDriver: response.data.isDriver || false,
          driverRating: response.data.driverRating,
          passengerRating: response.data.passengerRating,
          lastSeen: response.data.lastSeen
        });
      }
    } catch (error) {
      console.error('Error loading partner info:', error);
      // Fallback: try to get from messages
      try {
        const msgResponse: any = await apiService.get(`/messages/conversation/${partnerId}?limit=1`);
        if (msgResponse && Array.isArray(msgResponse) && msgResponse.length > 0) {
          const firstMessage = msgResponse[0];
          const partnerData = firstMessage.sender?.id === partnerId ? firstMessage.sender : firstMessage.receiver;

          setPartner({
            id: partnerId,
            firstName: partnerData?.firstName || 'User',
            lastName: partnerData?.lastName || '',
            profilePicture: partnerData?.profilePicture,
            isDriver: false
          });
        }
      } catch (err) {
        console.error('Error fetching partner from messages:', err);
      }
    }
  };

  // Fetch context details
  const fetchContextDetails = async () => {
    if (!contextId || !contextType) return;

    try {
      let endpoint = '';
      if (contextType === 'ride') {
        endpoint = `/rides/${contextId}`;
      } else if (contextType === 'request') {
        endpoint = `/requests/${contextId}`;
      } else if (contextType === 'bid') {
        endpoint = `/bids/${contextId}`;
      } else if (contextType === 'support') {
        endpoint = `/support/tickets/${contextId}`;
      }

      if (endpoint) {
        const response: any = await apiService.get(endpoint);
        if (response.success && response.data) {
          let detail = response.data.ride || response.data.ticket || response.data.bid || response.data;
          
          // Normalize bid details for route header display
          if (response.data.bid) {
            detail = {
              ...response.data.bid,
              originCity: response.data.bid.request?.originCity,
              destinationCity: response.data.bid.request?.destinationCity
            };
          }
          
          setContextDetails(detail);
        }
      }
    } catch (err) {
      console.error('Error fetching context details:', err);
    }
  };

  // Fetch messages
  const fetchMessages = async () => {
    if (!partnerId) return;

    try {
      setLoading(true);
      setError(null);
      const url = `/messages/conversation/${partnerId}${contextId ? `?contextId=${contextId}&contextType=${contextType}` : ''}`;
      const response: any = await apiService.get(url);

      // Handle both direct array response and wrapped response
      const messagesArray = Array.isArray(response) ? response : (response.data || []);

      if (Array.isArray(messagesArray)) {
        // Sort messages by timestamp (oldest first)
        const sortedMessages = [...messagesArray].sort((a, b) =>
          new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
        );

        setMessages(sortedMessages);
        setHasMoreMessages(sortedMessages.length >= 20);

        // If this is a support chat, fetch ticket status
        if (contextType === 'support' && contextId) {
          try {
            const ticketResp: any = await apiService.get(`/support/tickets/${contextId}`);
            if (ticketResp.success && ticketResp.data.ticket) {
              setSupportTicketStatus(ticketResp.data.ticket.status);
            }
          } catch (ticketErr) {
            console.error('Error fetching ticket status:', ticketErr);
          }
        }

        // Set oldest timestamp for pagination
        if (sortedMessages.length > 0) {
          setOldestTimestamp(sortedMessages[0].createdAt);
        } else {
          setOldestTimestamp(null);
        }

        // Mark unread messages as delivered and read
        const unreadMessageIds = sortedMessages
          .filter((msg: ApiMessage) => msg.receiverId === user?.id && msg.status !== 'READ')
          .map((msg: ApiMessage) => msg.id);

        if (unreadMessageIds.length > 0) {
          markAsDelivered(unreadMessageIds);
          markAsRead(unreadMessageIds);
        }

        // Scroll to bottom instantly on initial load
        setTimeout(() => {
          scrollToBottomInstant();
        }, 100);
      } else {
        console.warn('Unexpected response format:', response);
        setMessages([]);
      }
    } catch (err: any) {
      console.error('Error fetching messages:', err);
      setError(err.response?.data?.error || err.message || 'Failed to load messages');
      setMessages([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  };

  // Load older messages (pagination)
  const loadOlderMessages = useCallback(async () => {
    if (loadingOlder || !hasMoreMessages || !oldestTimestamp || !partnerId) return;

    try {
      setLoadingOlder(true);
      const response: any = await apiService.get(`/messages/conversation/${partnerId}`, {
        params: {
          before: oldestTimestamp,
          limit: 20,
        },
      });

      const olderMessages = Array.isArray(response) ? response : (response.data || []);

      if (olderMessages.length === 0) {
        setHasMoreMessages(false);
        return;
      }

      // Sort older messages by timestamp
      const sortedOlder = [...olderMessages].sort((a, b) =>
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
      );

      // Update oldest timestamp
      const newOldest = sortedOlder[0].createdAt;
      setOldestTimestamp(newOldest);

      // Prepend older messages (they go to the beginning)
      setMessages(prev => {
        const combined = [...sortedOlder, ...prev];
        // Remove duplicates
        const unique = combined.filter((msg, index, self) =>
          index === self.findIndex(m => m.id === msg.id)
        );
        return unique;
      });

      setHasMoreMessages(olderMessages.length >= 20);
    } catch (error) {
      console.error('Error loading older messages:', error);
    } finally {
      setLoadingOlder(false);
    }
  }, [loadingOlder, hasMoreMessages, oldestTimestamp, partnerId]);

  // Initial load
  useEffect(() => {
    if (partnerId) {
      checkConsent();
      checkBlockStatus();
      fetchPartner();
      fetchContextDetails();
      fetchMessages();
    }
  }, [partnerId]);

  // Listen for incoming messages
  useEffect(() => {
    if (!partnerId || !hasConsent) return;

    const unsubscribe = onMessageReceive((message: SocketMessage) => {
      // Only process messages for current conversation partner AND correct context
      const isPartner = message.senderId === partnerId || message.receiverId === partnerId;
      const isCorrectContext = (!contextId && !message.contextId) || (message.contextId === contextId);

      if (isPartner && isCorrectContext) {
        // Convert SocketMessage to ApiMessage format
        const apiMessage: ApiMessage = {
          ...message,
          createdAt: typeof message.createdAt === 'string' ? message.createdAt : message.createdAt.toISOString(),
          deliveredAt: message.deliveredAt ? (typeof message.deliveredAt === 'string' ? message.deliveredAt : message.deliveredAt.toISOString()) : undefined,
          readAt: message.readAt ? (typeof message.readAt === 'string' ? message.readAt : message.readAt.toISOString()) : undefined
        };

        // Check if message already exists to avoid duplicates
        setMessages(prev => {
          const exists = prev.some(msg => msg.id === apiMessage.id);
          if (exists) {
            console.log('Message already exists, skipping:', apiMessage.id);
            return prev;
          }
          console.log('Adding new message from socket:', apiMessage.id, apiMessage.content);

          // Add new message at the end (newest)
          const updated = [...prev, apiMessage];

          // Auto-scroll only if user is at bottom or message is from current user
          const isFromCurrentUser = message.senderId === user?.id;
          if (isFromCurrentUser || shouldAutoScrollRef.current) {
            setTimeout(() => {
              scrollToBottom();
            }, 50);
          }

          return updated;
        });

        // Mark as read immediately if we're viewing this conversation
        if (message.receiverId === user?.id) {
          markAsRead([message.id]);
        }
      }
    });

    return unsubscribe;
  }, [partnerId, hasConsent, onMessageReceive, user?.id, markAsRead, scrollToBottom]);

  // Listen for message status updates
  useEffect(() => {
    const unsubscribe = onMessageStatusUpdate((data) => {
      setMessages(prev => prev.map(msg =>
        msg.id === data.messageId
          ? { ...msg, status: data.status as 'SENT' | 'DELIVERED' | 'READ' }
          : msg
      ));
    });

    return unsubscribe;
  }, [onMessageStatusUpdate]);

  // Listen for typing indicators
  useEffect(() => {
    if (!partnerId) return;

    const unsubscribe = onTypingIndicator((data) => {
      if (data.userId === partnerId && data.contextId === contextId) {
        if (data.isTyping) {
          setTypingUsers(prev => new Set(prev).add(data.userId));
        } else {
          setTypingUsers(prev => {
            const newSet = new Set(prev);
            newSet.delete(data.userId);
            return newSet;
          });
        }
      }
    });

    return unsubscribe;
  }, [partnerId, contextId, onTypingIndicator]);

  // Auto-scroll when new messages are added (only if user is at bottom)
  // This handles cases where messages are added via state updates
  const prevMessagesLengthRef = useRef(messages.length);
  useEffect(() => {
    if (messages.length === 0) {
      prevMessagesLengthRef.current = 0;
      return;
    }

    // Only scroll if messages count increased and user is at bottom
    if (messages.length > prevMessagesLengthRef.current && shouldAutoScrollRef.current) {
      const timeoutId = setTimeout(() => {
        scrollToBottom();
      }, 50);

      prevMessagesLengthRef.current = messages.length;
      return () => clearTimeout(timeoutId);
    }

    prevMessagesLengthRef.current = messages.length;
  }, [messages.length, scrollToBottom]);

  // Handle send message
  const handleSendMessage = async () => {
    if (!messageInput.trim() || !partnerId || sending || !hasConsent) return;

    const content = messageInput.trim();
    setMessageInput('');
    setSending(true);

    // Stop typing indicator
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    stopTyping(partnerId, contextId);

    try {
      const response = await sendMessage({
        receiverId: partnerId,
        content,
        contextType,
        contextId
      });

      if (response.success && response.message) {
        // Convert SocketMessage to ApiMessage format and add to state
        const apiMessage: ApiMessage = {
          ...response.message,
          createdAt: typeof response.message.createdAt === 'string'
            ? response.message.createdAt
            : (response.message.createdAt instanceof Date
              ? response.message.createdAt.toISOString()
              : new Date().toISOString()),
          deliveredAt: response.message.deliveredAt
            ? (typeof response.message.deliveredAt === 'string'
              ? response.message.deliveredAt
              : response.message.deliveredAt.toISOString())
            : undefined,
          readAt: response.message.readAt
            ? (typeof response.message.readAt === 'string'
              ? response.message.readAt
              : response.message.readAt.toISOString())
            : undefined
        };
        console.log('📤 Message sent successfully, adding to state:', apiMessage.id, apiMessage.content);
        setMessages(prev => {
          const exists = prev.some(msg => msg.id === apiMessage.id);
          if (exists) {
            console.log('Message already exists in state, skipping');
            return prev;
          }
          // Add new message at the end (newest)
          return [...prev, apiMessage];
        });
        // Always scroll when user sends a message
        setTimeout(() => {
          scrollToBottom(true);
        }, 50);
      } else if (response.error) {
        if (response.rateLimitExceeded) {
          alert('Please wait for a response before sending more messages.');
        } else if (response.flagged) {
          alert('Message blocked: Please maintain professional and respectful language.');
        } else {
          alert(response.error);
        }
        setMessageInput(content); // Restore message
      }
    } catch (err: any) {
      console.error('Error sending message:', err);
      alert('Failed to send message');
      setMessageInput(content); // Restore message
    } finally {
      setSending(false);
    }
  };

  // Handle typing
  const handleTyping = (e: React.ChangeEvent<HTMLInputElement>) => {
    setMessageInput(e.target.value);

    if (!partnerId) return;

    // Clear previous timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    // Start typing
    if (e.target.value.length > 0) {
      startTyping(partnerId, contextId);

      // Stop typing after 3 seconds of inactivity
      typingTimeoutRef.current = setTimeout(() => {
        stopTyping(partnerId, contextId);
      }, 3000);
    } else {
      stopTyping(partnerId, contextId);
    }
  };

  // Handle key press
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Handle consent accepted
  const handleConsentAccepted = () => {
    setShowLegalGate(false);
    setHasConsent(true);
    if (partnerId) {
      fetchMessages();
    }
    messageInputRef.current?.focus();
  };

  // Helper function to get date label (Today, Yesterday, or date)
  const getDateLabel = (date: string) => {
    const messageDate = new Date(date);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    const messageDateStr = messageDate.toDateString();
    const todayStr = today.toDateString();
    const yesterdayStr = yesterday.toDateString();

    if (messageDateStr === todayStr) {
      return 'Today';
    } else if (messageDateStr === yesterdayStr) {
      return 'Yesterday';
    } else {
      return formatDate(date, 'MMM dd, yyyy');
    }
  };

  // Helper function to check if we need to show date separator
  const shouldShowDateSeparator = (currentMessage: ApiMessage, previousMessage: ApiMessage | null) => {
    if (!previousMessage) return true;

    const currentDate = new Date(currentMessage.createdAt).toDateString();
    const previousDate = new Date(previousMessage.createdAt).toDateString();

    return currentDate !== previousDate;
  };

  // Format time helper
  const formatTime = (date: string) => {
    return format(new Date(date), 'HH:mm');
  };

  // Render message component
  const renderMessage = (message: ApiMessage, index: number) => {
    const isOwnMessage = message.senderId === user?.id;
    const previousMessage = index > 0 ? messages[index - 1] : null;
    const showDateSeparator = shouldShowDateSeparator(message, previousMessage);

    const getStatusIcon = () => {
      if (!isOwnMessage) return null;

      switch (message.status) {
        case 'READ':
          return <DoneAllIcon sx={{ fontSize: 14, color: 'success.main' }} />;
        case 'DELIVERED':
          return <DoneAllIcon sx={{ fontSize: 14, color: 'text.secondary' }} />;
        case 'SENT':
          return <CheckIcon sx={{ fontSize: 14, color: 'text.secondary' }} />;
        default:
          return null;
      }
    };

    return (
      <React.Fragment key={message.id}>
        {showDateSeparator && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              my: 2,
              px: 2
            }}
          >
            <Box sx={{ flex: 1, height: 1, bgcolor: 'divider' }} />
            <Typography
              variant="caption"
              sx={{
                px: 2,
                color: 'text.secondary',
                fontSize: '0.875rem',
                fontWeight: 500
              }}
            >
              {getDateLabel(message.createdAt)}
            </Typography>
            <Box sx={{ flex: 1, height: 1, bgcolor: 'divider' }} />
          </Box>
        )}
        <Box
          sx={{
            display: 'flex',
            justifyContent: isOwnMessage ? 'flex-end' : 'flex-start',
            mb: 1.5,
            px: 2
          }}
        >
          <Box
            sx={{
              maxWidth: '80%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: isOwnMessage ? 'flex-end' : 'flex-start'
            }}
          >
            <Paper
              elevation={0}
              sx={{
                px: 2,
                py: 1.25,
                borderRadius: 2,
                borderBottomRightRadius: isOwnMessage ? 0.5 : 2,
                borderBottomLeftRadius: isOwnMessage ? 2 : 0.5,
                bgcolor: isOwnMessage ? 'primary.main' : 'background.paper',
                color: isOwnMessage ? 'primary.contrastText' : 'text.primary'
              }}
            >
              <TranslatingText
                text={message.content}
                variant="body1"
                sx={{
                  fontSize: '1rem',
                  lineHeight: 1.5,
                  wordBreak: 'break-word',
                  whiteSpace: 'pre-wrap',
                  color: isOwnMessage ? 'primary.contrastText' : 'text.primary'
                }}
              />
            </Paper>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                mt: 0.5,
                px: 0.5
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontSize: '0.75rem',
                  color: 'text.secondary'
                }}
              >
                {formatTime(message.createdAt)}
              </Typography>
              {getStatusIcon()}
              {!isOwnMessage && (
                <IconButton
                  size="small"
                  onClick={() => {
                    setSelectedMessageForReport(message.id);
                    setReportModalOpen(true);
                  }}
                  sx={{ p: 0.5, ml: 0.5 }}
                >
                  <ReportIcon sx={{ fontSize: 12, color: 'text.secondary' }} />
                </IconButton>
              )}
            </Box>
          </Box>
        </Box>
      </React.Fragment>
    );
  };

  // Render empty state
  const renderEmptyState = () => (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        flex: 1,
        py: 8,
        px: 4
      }}
    >
      <ChatBubbleIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
      <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1, fontSize: '1.25rem' }}>
        Start a conversation
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '1rem', textAlign: 'center' }}>
        Say hello to {partnerName?.split(' ')[0] || partner?.firstName || 'them'}!
      </Typography>
    </Box>
  );

  // Handle menu
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchor(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  // Handle report user
  const handleReportUser = () => {
    // For general user report, we report the latest message if any
    if (messages.length > 0) {
      // Find latest message from partner
      const latestFromPartner = [...messages].reverse().find(m => m.senderId === partnerId);
      if (latestFromPartner) {
        setSelectedMessageForReport(latestFromPartner.id);
      } else {
        // If no messages from partner, just report the first message in thread
        setSelectedMessageForReport(messages[0].id);
      }
    }
    setReportModalOpen(true);
    handleMenuClose();
  };

  // Handle mute
  const handleMute = async () => {
    try {
      await apiService.post('/message-actions/mute', {
        threadPartnerId: partnerId,
        contextId,
        duration: 24 // 24 hours
      });
      alert('Conversation muted for 24 hours');
      handleMenuClose();
    } catch (err) {
      console.error('Error muting conversation:', err);
      alert('Failed to mute conversation');
    }
  };

  // Handle block
  const handleBlock = async () => {
    if (!window.confirm('Are you sure you want to block this user? They will no longer be able to message you.')) {
      return;
    }

    try {
      await apiService.post('/message-actions/block', {
        threadPartnerId: partnerId
      });
      setIsBlocked(true);
      alert('User blocked successfully');
      handleMenuClose();
      navigate('/inbox');
    } catch (err) {
      console.error('Error blocking user:', err);
      alert('Failed to block user');
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  if (isBlocked) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="warning" sx={{ fontSize: '1.5rem', '& .MuiAlert-message': { fontSize: '1.5rem' } }}>
          This conversation is blocked. You cannot send or receive messages from this user.
        </Alert>
        <Button variant="contained" onClick={() => navigate('/inbox')} sx={{ mt: 2, fontSize: '1.5rem' }}>
          Back to Inbox
        </Button>
      </Container>
    );
  }

  return (
    <Container
      maxWidth="md"
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        py: 0,
        px: { xs: 1, sm: 2 },
      }}
    >
      <Paper
        elevation={3}
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: { xs: 0, sm: 2 },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: 2,
            borderBottom: 1,
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            gap: 2
          }}
        >
          <IconButton onClick={() => navigate('/inbox')}>
            <BackIcon />
          </IconButton>

          <Badge
            overlap="circular"
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            badgeContent={
              partnerId && isOnline(partnerId) ? (
                <OnlineDotIcon
                  sx={{
                    width: 12,
                    height: 12,
                    color: 'success.main',
                    bgcolor: 'white',
                    borderRadius: '50%'
                  }}
                />
              ) : null
            }
          >
            <Avatar
              src={partner?.profilePicture}
              sx={{ width: 40, height: 40, cursor: 'pointer' }}
              onClick={() => partnerId && openProfile(partnerId)}
            >
              {partner ? `${partner.firstName[0]}${partner.lastName[0]}` : <PersonIcon />}
            </Avatar>
          </Badge>

          <Box sx={{ flexGrow: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h6" fontWeight="bold" sx={{ fontSize: '1rem' }}>
                {partner?.preferredName || partner?.firstName} {partner?.lastName || ''}
              </Typography>
              {partner?.isDriver && (
                <VerifiedIcon sx={{ fontSize: '1rem', color: 'primary.main' }} />
              )}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                {partnerId && isOnline(partnerId)
                  ? 'Online'
                  : partner?.lastSeen
                    ? `Last seen ${formatRelativeDate(partner.lastSeen)}`
                    : 'Offline'}
              </Typography>
              {contextDetails && (
                <>
                  <Typography variant="caption" color="text.secondary">•</Typography>
                  <Typography variant="caption" sx={{ fontSize: '0.75rem', color: 'primary.main', fontWeight: 500 }}>
                    {contextDetails.originCity?.name} → {contextDetails.destinationCity?.name}
                  </Typography>
                </>
              )}
            </Box>
          </Box>


          <IconButton onClick={handleMenuOpen}>
            <MoreVertIcon />
          </IconButton>
        </Box>

        {/* Messages */}
        <Box
          ref={messagesContainerRef}
          sx={{
            flexGrow: 1,
            overflowY: 'auto',
            bgcolor: '#f5f5f5',
            position: 'relative',
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              background: 'transparent',
            },
            '&::-webkit-scrollbar-thumb': {
              background: 'rgba(0,0,0,0.2)',
              borderRadius: '4px',
            },
          }}
          onScroll={(e) => {
            const target = e.currentTarget;
            const threshold = 100;
            const isAtBottom = target.scrollHeight - target.scrollTop - target.clientHeight < threshold;
            setIsUserAtBottom(isAtBottom);
            shouldAutoScrollRef.current = isAtBottom;

            // Load older messages when scrolling near top
            if (target.scrollTop < 200 && hasMoreMessages && !loadingOlder) {
              loadOlderMessages();
            }
          }}
        >
          {contextType === 'support' && (
            <Box sx={{ p: 4, mb: 4 }}>
              <Alert severity="info" icon={false} sx={{ '& .MuiAlert-message': { width: '100%' } }}>
                <Typography variant="h5" fontWeight="bold" display="block" gutterBottom sx={{ fontSize: '2rem' }}>
                  LEGAL DISCLAIMER / AVIS JURIDIQUE
                </Typography>
                <Typography variant="body1" display="block" sx={{ fontSize: '1.5rem', lineHeight: 1.6 }}>
                  This mediation is an internal attempt to resolve disputes and does not constitute a legal proceeding.
                  Cette médiation est une tentative interne de résolution des litiges et ne constitue pas une procédure judiciaire.
                </Typography>
              </Alert>
            </Box>
          )}

          {loadingOlder && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 2 }}>
              <CircularProgress size={20} />
              <Typography variant="caption" sx={{ ml: 1, fontSize: '0.875rem' }}>
                Loading older messages...
              </Typography>
            </Box>
          )}

          {messages.length === 0 && !loading ? (
            renderEmptyState()
          ) : (
            <>
              {messages.map((message, index) => renderMessage(message, index))}
              {typingUsers.size > 0 && <TypingIndicator userName={partner?.firstName || 'User'} />}
              <div ref={messagesEndRef} />
            </>
          )}
        </Box>

        {/* Connection Status */}
        {!isConnected && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              py: 1,
              px: 2,
              bgcolor: 'warning.light',
              color: 'warning.contrastText'
            }}
          >
            <WarningIcon sx={{ fontSize: 16 }} />
            <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
              Connecting...
            </Typography>
          </Box>
        )}

        {/* Input */}
        <Box sx={{ p: 1.5, borderTop: 1, borderColor: 'divider', bgcolor: '#F5F5F5' }}>
          {supportTicketStatus === 'RESOLVED' ? (
            <Box sx={{ textAlign: 'center', p: 1 }}>
              <Typography variant="body2" color="text.secondary">
                This mediation case is closed. You can no longer send messages.
              </Typography>
            </Box>
          ) : (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                bgcolor: 'background.paper',
                borderRadius: '20px',
                px: 1.5,
                py: 1
              }}
            >
              <TextField
                fullWidth
                placeholder={hasConsent ? "Type a message" : "Accept disclaimer to send messages"}
                value={messageInput}
                onChange={handleTyping}
                onKeyPress={handleKeyPress}
                disabled={!hasConsent || sending}
                inputRef={messageInputRef}
                multiline
                maxRows={4}
                variant="standard"
                InputProps={{
                  disableUnderline: true,
                  sx: {
                    fontSize: '1rem',
                    '& input': {
                      fontSize: '1rem',
                    },
                  }
                }}
                sx={{
                  '& .MuiInputBase-root': {
                    fontSize: '1rem',
                  }
                }}
              />
              <IconButton
                onClick={handleSendMessage}
                disabled={!messageInput.trim() || !hasConsent || sending}
                sx={{
                  width: 38,
                  height: 38,
                  bgcolor: (messageInput.trim().length > 0 && !sending && hasConsent)
                    ? 'primary.main'
                    : '#CFCFCF',
                  color: 'white',
                  '&:hover': {
                    bgcolor: (messageInput.trim().length > 0 && !sending && hasConsent)
                      ? 'primary.dark'
                      : '#CFCFCF',
                  },
                  '&.Mui-disabled': {
                    bgcolor: '#CFCFCF',
                    color: 'white'
                  }
                }}
              >
                {sending ? (
                  <CircularProgress size={20} sx={{ color: 'white' }} />
                ) : (
                  <SendIcon sx={{ fontSize: 20 }} />
                )}
              </IconButton>
            </Box>
          )}
        </Box>
      </Paper>

      {/* Legal Gate Modal */}
      {
        contextType && contextId && (
          <LegalGateModal
            open={showLegalGate}
            onClose={() => { }}
            onAccept={handleConsentAccepted}
            contextType={contextType}
            contextId={contextId}
          />
        )
      }

      {/* Kebab Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleMute} sx={{ fontSize: '1.3125rem' }}>
          <MuteIcon sx={{ mr: 1, fontSize: '1.3125rem' }} />
          Mute (24h)
        </MenuItem>
        <MenuItem onClick={handleBlock} sx={{ fontSize: '1.3125rem' }}>
          <BlockIcon sx={{ mr: 1, fontSize: '1.3125rem' }} />
          Block User
        </MenuItem>
        <MenuItem onClick={handleReportUser} sx={{ fontSize: '1.3125rem' }}>
          <ReportIcon sx={{ mr: 1, fontSize: '1.3125rem' }} />
          Report User
        </MenuItem>
      </Menu>

      {/* Report Message Modal */}
      <ReportMessageModal
        open={reportModalOpen}
        onClose={() => {
          setReportModalOpen(false);
          setSelectedMessageForReport(null);
        }}
        messageId={selectedMessageForReport}
      />
    </Container >
  );
};

export default ChatPage;

